import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { adminApi } from '../../services/api';
import { PageTooltip } from '../components/TooltipSystem';
import { BrandColorPicker } from '../components/BrandColorPicker';
import {
  Plus, Trash2, Save, Eye, Send, ArrowUp, ArrowDown, Type,
  Image, Link2, Minus, Layout, ChevronLeft, Mail, GripVertical,
  Loader2, Copy, X, Settings2,
} from 'lucide-react';

const BLOCK_TYPES = [
  { type: 'header', label: 'Header', icon: Layout, desc: 'Intestazione con logo e titolo' },
  { type: 'text', label: 'Testo', icon: Type, desc: 'Paragrafo di testo libero' },
  { type: 'image', label: 'Immagine', icon: Image, desc: 'Immagine con URL' },
  { type: 'button', label: 'Pulsante', icon: Link2, desc: 'Call-to-action con link' },
  { type: 'divider', label: 'Separatore', icon: Minus, desc: 'Linea di separazione' },
  { type: 'footer', label: 'Footer', icon: Layout, desc: 'Testo in fondo all\'email' },
];

const DEFAULT_BLOCKS = [
  { id: '1', type: 'header', content: 'Il Tuo Locale', settings: { bgColor: '#D44E20', textColor: '#FFFFFF', logoUrl: '' } },
  { id: '2', type: 'text', content: 'Ciao {{nome}},\n\nAbbiamo una novità per te!', settings: { align: 'left', fontSize: '15', textColor: '#333333' } },
  { id: '3', type: 'button', content: 'Scopri di più', settings: { bgColor: '#D44E20', textColor: '#FFFFFF', url: '{{link}}' } },
  { id: '4', type: 'footer', content: 'Hai ricevuto questa email perché sei iscritto al programma fedeltà.\nPer disiscriverti, rispondi a questa email.', settings: { textColor: '#999999' } },
];

export default function AdminEmailTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [emailConfigured, setEmailConfigured] = useState(false);

  const loadTemplates = useCallback(() => {
    adminApi.get('/email/templates').then(r => setTemplates(r.data)).catch(() => {});
    adminApi.get('/email/status').then(r => setEmailConfigured(r.data.configured)).catch(() => {});
  }, []);

  useEffect(() => {
    loadTemplates();
    setLoading(false);
  }, [loadTemplates]);

  const createNew = () => {
    setEditing({
      template_id: null,
      name: '',
      subject: '',
      blocks: JSON.parse(JSON.stringify(DEFAULT_BLOCKS)),
      preview_text: '',
    });
  };

  const editTemplate = (t) => {
    setEditing({ ...t, blocks: JSON.parse(JSON.stringify(t.blocks)) });
  };

  const deleteTemplate = async (tid) => {
    if (!window.confirm('Eliminare questo template?')) return;
    await adminApi.delete(`/email/templates/${tid}`);
    toast.success('Template eliminato');
    loadTemplates();
  };

  const saveTemplate = async () => {
    if (!editing.name.trim()) { toast.error('Inserisci un nome'); return; }
    if (!editing.subject.trim()) { toast.error('Inserisci un oggetto'); return; }
    setSaving(true);
    try {
      const body = { name: editing.name, subject: editing.subject, blocks: editing.blocks, preview_text: editing.preview_text };
      if (editing.template_id) {
        await adminApi.put(`/email/templates/${editing.template_id}`, body);
        toast.success('Template aggiornato');
      } else {
        const res = await adminApi.post('/email/templates', body);
        editing.template_id = res.data.template_id;
        toast.success('Template creato');
      }
      loadTemplates();
    } catch { toast.error('Errore salvataggio'); }
    setSaving(false);
  };

  const preview = async () => {
    try {
      const res = await adminApi.post('/email/preview', {
        name: editing.name || 'Preview', subject: editing.subject || 'Test',
        blocks: editing.blocks, preview_text: editing.preview_text,
      });
      setPreviewHtml(res.data.html);
      setShowPreview(true);
    } catch { toast.error('Errore preview'); }
  };

  const sendTest = async () => {
    if (!editing.template_id) { toast.error('Salva prima il template'); return; }
    if (!testEmail.trim()) { toast.error('Inserisci email'); return; }
    setSendingTest(true);
    try {
      await adminApi.post('/email/send-test', { template_id: editing.template_id, to_email: testEmail });
      toast.success(`Email di test inviata a ${testEmail}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore invio'); }
    setSendingTest(false);
  };

  if (editing) {
    return (
      <TemplateEditor
        template={editing} setTemplate={setEditing}
        onSave={saveTemplate} saving={saving}
        onPreview={preview} onBack={() => { setEditing(null); loadTemplates(); }}
        testEmail={testEmail} setTestEmail={setTestEmail}
        onSendTest={sendTest} sendingTest={sendingTest}
        showPreview={showPreview} previewHtml={previewHtml}
        setShowPreview={setShowPreview} emailConfigured={emailConfigured}
      />
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="email-templates-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1A1A]">Template Email</h1>
          <p className="text-sm text-[#64748B] mt-1">Crea e gestisci i template per le comunicazioni email</p>
        </div>
        <div className="flex items-center gap-3">
          {emailConfigured && <span className="text-[10px] font-medium text-green-700 bg-green-50 px-2 py-1 rounded-lg flex items-center gap-1"><Mail size={10} /> Email attiva</span>}
          <button onClick={createNew} data-testid="create-template-btn"
            className="flex items-center gap-2 px-4 py-2.5 bg-[#D44E20] text-white rounded-xl font-semibold text-sm hover:bg-[#c04018] transition-all">
            <Plus size={16} /> Nuovo Template
          </button>
        </div>
      </div>

      {templates.length === 0 ? (
        <div className="bg-gray-50 rounded-2xl p-12 text-center">
          <Mail className="mx-auto mb-4 text-[#ccc]" size={48} />
          <p className="text-[#64748B] text-sm mb-1">Nessun template email creato</p>
          <p className="text-[#aaa] text-xs">Crea il tuo primo template per iniziare a inviare email personalizzate</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map(t => (
            <div key={t.template_id} className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm hover:shadow-md transition-all" data-testid={`template-card-${t.template_id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="min-w-0">
                  <p className="text-sm font-bold text-[#1A1A1A] truncate">{t.name}</p>
                  <p className="text-xs text-[#64748B] truncate mt-0.5">Oggetto: {t.subject}</p>
                </div>
                <button onClick={() => deleteTemplate(t.template_id)} className="text-[#ccc] hover:text-red-500 transition-colors flex-shrink-0">
                  <Trash2 size={14} />
                </button>
              </div>
              <p className="text-[10px] text-[#aaa] mb-3">{t.blocks?.length || 0} blocchi</p>
              <button onClick={() => editTemplate(t)} className="w-full py-2 rounded-xl bg-slate-50 text-xs font-semibold text-[#64748B] hover:bg-slate-100 transition-colors">
                Modifica
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Variables reference */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
        <h3 className="text-sm font-bold text-[#1A1A1A] mb-3">Variabili disponibili</h3>
        <div className="flex flex-wrap gap-2">
          {['{{nome}}', '{{punti}}', '{{tier}}', '{{codice_coupon}}', '{{link}}', '{{locale}}'].map(v => (
            <code key={v} className="bg-gray-50 text-[#D44E20] text-xs px-2.5 py-1 rounded-lg font-mono">{v}</code>
          ))}
        </div>
        <p className="text-[10px] text-[#aaa] mt-2">Inserisci queste variabili nel contenuto dei blocchi. Verranno sostituite automaticamente con i dati del cliente.</p>
      </div>
    </div>
  );
}

function TemplateEditor({ template, setTemplate, onSave, saving, onPreview, onBack, testEmail, setTestEmail, onSendTest, sendingTest, showPreview, previewHtml, setShowPreview, emailConfigured }) {
  const updateBlock = (idx, field, value) => {
    const blocks = [...template.blocks];
    if (field.startsWith('settings.')) {
      const key = field.split('.')[1];
      blocks[idx] = { ...blocks[idx], settings: { ...blocks[idx].settings, [key]: value } };
    } else {
      blocks[idx] = { ...blocks[idx], [field]: value };
    }
    setTemplate({ ...template, blocks });
  };

  const addBlock = (type) => {
    const defaults = {
      header: { content: 'Titolo', settings: { bgColor: '#D44E20', textColor: '#FFFFFF', logoUrl: '' } },
      text: { content: 'Scrivi qui il tuo testo...', settings: { align: 'left', fontSize: '15', textColor: '#333333' } },
      image: { content: '', settings: { imageUrl: '', alt: 'Immagine' } },
      button: { content: 'Clicca qui', settings: { bgColor: '#D44E20', textColor: '#FFFFFF', url: '' } },
      divider: { content: '', settings: { color: '#e0e0e0' } },
      footer: { content: 'Testo footer', settings: { textColor: '#999999' } },
    };
    const block = { id: Date.now().toString(), type, ...defaults[type] };
    setTemplate({ ...template, blocks: [...template.blocks, block] });
  };

  const removeBlock = (idx) => {
    setTemplate({ ...template, blocks: template.blocks.filter((_, i) => i !== idx) });
  };

  const moveBlock = (idx, dir) => {
    const blocks = [...template.blocks];
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= blocks.length) return;
    [blocks[idx], blocks[newIdx]] = [blocks[newIdx], blocks[idx]];
    setTemplate({ ...template, blocks });
  };

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="template-editor">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-[#64748B] hover:text-[#1A1A1A] transition-colors">
          <ChevronLeft size={16} /> Torna ai template
        </button>
        <div className="flex items-center gap-2">
          <button onClick={onPreview} className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-200 text-xs font-semibold text-[#64748B] hover:bg-slate-50 transition-colors" data-testid="preview-btn">
            <Eye size={14} /> Anteprima
          </button>
          <button onClick={onSave} disabled={saving} data-testid="save-template-btn"
            className="flex items-center gap-1.5 px-4 py-2 bg-[#D44E20] text-white rounded-xl text-xs font-bold hover:bg-[#c04018] transition-all disabled:opacity-50">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Salva
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Block editor */}
        <div className="lg:col-span-2 space-y-4">
          {/* Template meta */}
          <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-[#64748B] block mb-1">Nome template</label>
                <input value={template.name} onChange={e => setTemplate({ ...template, name: e.target.value })}
                  placeholder="Es: Newsletter mensile" data-testid="template-name"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:border-[#D44E20] focus:outline-none" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#64748B] block mb-1">Oggetto email</label>
                <input value={template.subject} onChange={e => setTemplate({ ...template, subject: e.target.value })}
                  placeholder="Es: Novità dal tuo locale!" data-testid="template-subject"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:border-[#D44E20] focus:outline-none" />
              </div>
            </div>
          </div>

          {/* Blocks */}
          <div className="space-y-3">
            {template.blocks.map((block, idx) => (
              <BlockEditor key={block.id} block={block} idx={idx}
                onChange={updateBlock} onRemove={removeBlock} onMove={moveBlock}
                total={template.blocks.length}
              />
            ))}
          </div>

          {/* Add block */}
          <div className="bg-white rounded-2xl border-2 border-dashed border-slate-200 p-4">
            <p className="text-xs font-semibold text-[#64748B] mb-3">Aggiungi blocco</p>
            <div className="flex flex-wrap gap-2">
              {BLOCK_TYPES.map(bt => {
                const Icon = bt.icon;
                return (
                  <button key={bt.type} onClick={() => addBlock(bt.type)} data-testid={`add-block-${bt.type}`}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-slate-50 border border-slate-100 text-xs font-medium text-[#64748B] hover:border-[#D44E20] hover:text-[#D44E20] transition-all">
                    <Icon size={13} /> {bt.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right: Test send panel */}
        <div className="space-y-4">
          {emailConfigured && template.template_id && (
            <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
              <h3 className="text-sm font-bold text-[#1A1A1A] mb-3 flex items-center gap-1.5"><Send size={14} /> Invia email di test</h3>
              <input value={testEmail} onChange={e => setTestEmail(e.target.value)}
                placeholder="tuaemail@esempio.com" data-testid="test-email-input"
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-sm mb-3 focus:border-[#D44E20] focus:outline-none" />
              <button onClick={onSendTest} disabled={sendingTest} data-testid="send-test-btn"
                className="w-full py-2.5 bg-[#3B82F6] text-white rounded-xl text-xs font-bold hover:bg-[#2563EB] transition-all disabled:opacity-50 flex items-center justify-center gap-1.5">
                {sendingTest ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Invia test
              </button>
            </div>
          )}

          <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
            <h3 className="text-sm font-bold text-[#1A1A1A] mb-3 flex items-center gap-1.5"><Settings2 size={14} /> Variabili</h3>
            <div className="space-y-1.5">
              {[
                { v: '{{nome}}', d: 'Nome cliente' },
                { v: '{{punti}}', d: 'Punti attuali' },
                { v: '{{tier}}', d: 'Livello fedeltà' },
                { v: '{{codice_coupon}}', d: 'Codice coupon' },
                { v: '{{link}}', d: 'Link bacheca' },
                { v: '{{locale}}', d: 'Nome locale' },
              ].map(({ v, d }) => (
                <div key={v} className="flex items-center justify-between py-1.5">
                  <code className="text-[11px] font-mono text-[#D44E20] bg-[#D44E20]/5 px-2 py-0.5 rounded">{v}</code>
                  <span className="text-[10px] text-[#64748B]">{d}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Preview modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowPreview(false)}>
          <div className="bg-white rounded-2xl max-w-[700px] w-full max-h-[85vh] overflow-hidden shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b">
              <h3 className="text-sm font-bold text-[#1A1A1A]">Anteprima email</h3>
              <button onClick={() => setShowPreview(false)} className="text-[#64748B] hover:text-[#1A1A1A]"><X size={18} /></button>
            </div>
            <div className="overflow-y-auto max-h-[75vh]">
              <iframe srcDoc={previewHtml} title="Preview" className="w-full border-0" style={{ height: '600px' }} data-testid="email-preview-iframe" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function BlockEditor({ block, idx, onChange, onRemove, onMove, total }) {
  const [expanded, setExpanded] = useState(true);
  const meta = BLOCK_TYPES.find(bt => bt.type === block.type) || {};
  const Icon = meta.icon || Type;

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-testid={`block-${idx}`}>
      <div className="flex items-center gap-2 px-4 py-3 bg-slate-50 border-b border-slate-100 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <GripVertical size={14} className="text-[#ccc]" />
        <Icon size={14} className="text-[#D44E20]" />
        <span className="text-xs font-semibold text-[#1A1A1A] flex-1">{meta.label || block.type}</span>
        <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
          {idx > 0 && <button onClick={() => onMove(idx, -1)} className="p-1 text-[#aaa] hover:text-[#1A1A1A]"><ArrowUp size={12} /></button>}
          {idx < total - 1 && <button onClick={() => onMove(idx, 1)} className="p-1 text-[#aaa] hover:text-[#1A1A1A]"><ArrowDown size={12} /></button>}
          <button onClick={() => onRemove(idx)} className="p-1 text-[#ccc] hover:text-red-500"><Trash2 size={12} /></button>
        </div>
      </div>

      {expanded && (
        <div className="p-4 space-y-3">
          {/* Content field */}
          {block.type !== 'divider' && (
            <div>
              <label className="text-[10px] font-semibold text-[#64748B] block mb-1">
                {block.type === 'button' ? 'Testo pulsante' : block.type === 'image' ? 'Alt text' : 'Contenuto'}
              </label>
              {block.type === 'text' || block.type === 'footer' ? (
                <textarea value={block.content} onChange={e => onChange(idx, 'content', e.target.value)}
                  rows={3} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm focus:border-[#D44E20] focus:outline-none resize-none" />
              ) : (
                <input value={block.content} onChange={e => onChange(idx, 'content', e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm focus:border-[#D44E20] focus:outline-none" />
              )}
            </div>
          )}

          {/* Type-specific settings */}
          {block.type === 'header' && (
            <div className="grid grid-cols-3 gap-2">
              <ColorInput label="Sfondo" value={block.settings.bgColor} onChange={v => onChange(idx, 'settings.bgColor', v)} />
              <ColorInput label="Testo" value={block.settings.textColor} onChange={v => onChange(idx, 'settings.textColor', v)} />
              <div>
                <label className="text-[10px] font-semibold text-[#64748B] block mb-1">Logo URL</label>
                <input value={block.settings.logoUrl || ''} onChange={e => onChange(idx, 'settings.logoUrl', e.target.value)}
                  placeholder="https://..." className="w-full bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5 text-[11px] focus:border-[#D44E20] focus:outline-none" />
              </div>
            </div>
          )}

          {block.type === 'text' && (
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-[10px] font-semibold text-[#64748B] block mb-1">Allineamento</label>
                <select value={block.settings.align || 'left'} onChange={e => onChange(idx, 'settings.align', e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5 text-[11px] focus:outline-none">
                  <option value="left">Sinistra</option><option value="center">Centro</option><option value="right">Destra</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] font-semibold text-[#64748B] block mb-1">Font size</label>
                <input type="number" value={block.settings.fontSize || '15'} onChange={e => onChange(idx, 'settings.fontSize', e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5 text-[11px] focus:outline-none" />
              </div>
              <ColorInput label="Colore" value={block.settings.textColor} onChange={v => onChange(idx, 'settings.textColor', v)} />
            </div>
          )}

          {block.type === 'image' && (
            <div>
              <label className="text-[10px] font-semibold text-[#64748B] block mb-1">URL immagine</label>
              <input value={block.settings.imageUrl || ''} onChange={e => onChange(idx, 'settings.imageUrl', e.target.value)}
                placeholder="https://..." className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm focus:border-[#D44E20] focus:outline-none" />
            </div>
          )}

          {block.type === 'button' && (
            <div className="grid grid-cols-3 gap-2">
              <ColorInput label="Sfondo" value={block.settings.bgColor} onChange={v => onChange(idx, 'settings.bgColor', v)} />
              <ColorInput label="Testo" value={block.settings.textColor} onChange={v => onChange(idx, 'settings.textColor', v)} />
              <div>
                <label className="text-[10px] font-semibold text-[#64748B] block mb-1">URL link</label>
                <input value={block.settings.url || ''} onChange={e => onChange(idx, 'settings.url', e.target.value)}
                  placeholder="https://..." className="w-full bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5 text-[11px] focus:border-[#D44E20] focus:outline-none" />
              </div>
            </div>
          )}

          {block.type === 'divider' && (
            <ColorInput label="Colore linea" value={block.settings.color} onChange={v => onChange(idx, 'settings.color', v)} />
          )}

          {block.type === 'footer' && (
            <ColorInput label="Colore testo" value={block.settings.textColor} onChange={v => onChange(idx, 'settings.textColor', v)} />
          )}
        </div>
      )}
    </div>
  );
}

function ColorInput({ label, value, onChange }) {
  return (
    <div>
      <PageTooltip page="email-templates" />
      <label className="text-[10px] font-semibold text-[#64748B] block mb-1">{label}</label>
      <BrandColorPicker value={value || '#333333'} onChange={onChange} size={28} />
    </div>
  );
}
